import { memo, useMemo, useCallback } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    Box,
    type SelectChangeEvent,
} from '@mui/material';
import { MULTI_SELECT_SX } from './filterStyles';

const SCORE_OPTIONS = [0, 1, 2, 3, 4, 5];

interface ScoreMultiSelectProps {
    /** Selected score value(s) */
    value: number | number[] | undefined;
    /** Callback when selection changes - receives array of scores or undefined */
    onChange: (scores: number[] | undefined) => void;
    /** Label for the select */
    label?: string;
    /** Component size */
    size?: 'small' | 'medium';
    /** Keep dropdown inside the DOM tree (required when used inside a modal with focus traps) */
    disablePortal?: boolean;
}

function toNumberArray(value: number | number[] | undefined): number[] {
    if (value === undefined || value === null) return [];
    if (Array.isArray(value)) return value;
    return [value];
}

function ScoreMultiSelectComponent({
    value,
    onChange,
    label = 'Score',
    size = 'small',
    disablePortal,
}: ScoreMultiSelectProps) {
    const selectedScores = useMemo(() => toNumberArray(value), [value]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<number[]>) => {
            const newValue = event.target.value as number[];
            onChange(newValue.length > 0 ? newValue : undefined);
        },
        [onChange],
    );

    const handleChipDelete = useCallback(
        (scoreToRemove: number) => (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            const newScores = selectedScores.filter(s => s !== scoreToRemove);
            onChange(newScores.length > 0 ? newScores : undefined);
        },
        [selectedScores, onChange],
    );

    return (
        <FormControl size={size} sx={MULTI_SELECT_SX}>
            <InputLabel>{label}</InputLabel>
            <Select
                multiple
                value={selectedScores}
                label={label}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                MenuProps={disablePortal ? { disablePortal: true } : undefined}
                renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(score => (
                            <Chip
                                key={score}
                                label={String(score)}
                                size="small"
                                onDelete={handleChipDelete(score)}
                                onMouseDown={e => e.stopPropagation()}
                            />
                        ))}
                    </Box>
                )}
            >
                {SCORE_OPTIONS.map(score => (
                    <MenuItem key={score} value={score}>
                        {score}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export const ScoreMultiSelect = memo(ScoreMultiSelectComponent);

/** Exported for testing and use in chip generation */
export { SCORE_OPTIONS };
